import React, { useMemo, useState, useCallback } from 'react'
import debounce from 'lodash/debounce'

import FieldLabel from '@react/react-spectrum/FieldLabel'
import Select from '@react/react-spectrum/Select'
import Textfield from '@react/react-spectrum/Textfield'
import Wait from '@react/react-spectrum/Wait'
import Heading from '@react/react-spectrum/Heading'
import RadioGroup from '@react/react-spectrum/RadioGroup'
import Radio from '@react/react-spectrum/Radio'
import { Form } from '@react/react-spectrum/Form'

import { useMyImsOrgs, useProject } from 'hooks'
import DatastreamPicker from 'components/DatastreamPicker'

import './SsrForm.scss'

const SsrFormWrapper = () => {
  const { project, updateProjectPart, canEdit, type } = useProject()
  const [integrations, setIntegrations] = useState(project.integrations || {})

  const updateIntegrations = useCallback(
    (updateFunction) => {
      let updatedIntegrations = integrations
      setIntegrations((i) => {
        updatedIntegrations = updateFunction(i)
        console.debug('Updated integrations:', updatedIntegrations)
        updateProjectPart('integrations', updatedIntegrations)
        return updatedIntegrations
      })
    },
    [integrations, updateProjectPart]
  )

  const updateIntegration = useCallback(
    (integrationName) => (updateParam) => {
      if (typeof updateParam === 'function') {
        updateIntegrations((i) => ({ ...i, [integrationName]: updateParam(i[integrationName]) }))
      } else {
        updateIntegrations((i) => ({ ...i, [integrationName]: updateParam }))
      }
    },
    [updateIntegrations]
  )

  if (type === 'ssr') {
    return (
      <div className='SsrFormWrapper'>
        <Form aria-labelledby='SsrFormWrapper'>
          <SsrForm integrations={integrations} canEdit={canEdit} updateIntegration={updateIntegration} />
        </Form>
      </div>
    )
  }
}

function SsrForm({ integrations, updateIntegration, canEdit }) {
  const { target, edge, imsOrg, aep } = integrations || {}
  const { loading: imsOrgsLoading, data: imsOrgs } = useMyImsOrgs()
  const {
    sandbox: sandboxLocal,
    abMbox: abMboxLocal,
    xtMbox: xtMboxLocal,
    setSandbox,
    setAbMbox,
    setXtMbox,
  } = useLocalFormStates({
    sandbox: aep?.sandbox,
    abMbox: target?.abMbox,
    xtMbox: target?.xtMbox,
    datastream: edge?.datastream,
  })

  const { updateEdge, updateTarget, updateIMSOrg, updateAEP } = useMemo(
    () => ({
      updateEdge: updateIntegration('edge'),
      updateTarget: debounce(updateIntegration('target'), 500),
      updateIMSOrg: updateIntegration('imsOrg'),
      updateAEP: debounce(updateIntegration('aep'), 500),
    }),
    [updateIntegration]
  )

  const imsOrgOptions = Array.isArray(imsOrgs) ? imsOrgs.map(({ id, name }) => ({ label: name, value: id })) : []

  const setImsOrg = (orgId) => {
    const { targetClientCode, tenantID, id, name } = imsOrgs.find((org) => org.id === orgId) || {}
    updateIMSOrg({ targetClientCode, tenantID, id, name })
  }

  return (
    <>
      {imsOrgsLoading && <Wait size='S' />}
      {!imsOrgsLoading && (
        <>
          <section>
            <FieldLabel label='IMS Org'>
              <Select value={imsOrg?.id || ''} options={imsOrgOptions} onChange={setImsOrg} disabled={!canEdit} />
            </FieldLabel>
            <FieldLabel label='Sandbox name'>
              <Textfield
                value={sandboxLocal || ''}
                onChange={(sandboxName) => {
                  updateAEP((l) => ({ ...(l || {}), sandbox: sandboxName }))
                  setSandbox(sandboxName)
                }}
                disabled={!canEdit}
              />
            </FieldLabel>
            <FieldLabel label='A/B Mbox'>
              <Textfield
                value={abMboxLocal || ''}
                onChange={(abMbox) => {
                  updateTarget((l) => ({ ...l, abMbox }))
                  setAbMbox(abMbox)
                }}
                disabled={!canEdit}
              />
            </FieldLabel>
            <FieldLabel label='XT Mbox'>
              <Textfield
                value={xtMboxLocal || ''}
                onChange={(xtMbox) => {
                  updateTarget((l) => ({ ...l, xtMbox }))
                  setXtMbox(xtMbox)
                }}
                disabled={!canEdit}
              />
            </FieldLabel>
            <FieldLabel label='Datastream' className='SsrForm__DatastreamPicker'>
              <DatastreamPicker
                allowInputWithEmptySandbox
                value={edge?.datastream}
                onChange={(datastream) => updateEdge((l) => ({ ...(l || {}), datastream }))}
                disabled={!canEdit}
              />
            </FieldLabel>
          </section>
          <section>
            <Heading size='4'>Fallback configuration</Heading>
            <em>
              By default, the SSR application uses Target to gather recommendations for the main part of the screen. If
              this fails, the application will use the following configuration.
            </em>
            <FieldLabel label='Recommendation fallback'>
              <RadioGroup
                selectedValue={target?.fallbackVariant || 'none'}
                onChange={(e) => updateIntegration('target')((l) => ({ ...(l || {}), fallbackVariant: e }))}
                disabled={!canEdit}
              >
                {fallbackOptions.map(({ label, value }) => (
                  <Radio key={value} label={label} value={value} disabled={!canEdit} />
                ))}
              </RadioGroup>
            </FieldLabel>
          </section>
        </>
      )}
    </>
  )
}

function useLocalFormStates({ sandbox, abMbox, xtMbox }) {
  const [_sandbox, setSandbox] = useState(sandbox)
  const [_abMbox, setAbMbox] = useState(abMbox)
  const [_xtMbox, setXtMbox] = useState(xtMbox)

  return { sandbox: _sandbox, abMbox: _abMbox, xtMbox: _xtMbox, setSandbox, setAbMbox, setXtMbox }
}

const fallbackOptions = [
  { label: 'Experience Decisioning', value: 'ode' },
  { label: 'Experience Decisioning, then default hardcoded fallback', value: 'ode-default' },
  { label: 'Default hardcoded fallback', value: 'default' },
  { label: 'None', value: 'none' },
]

export default SsrFormWrapper
